import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebaseClient'; // Replaced Supabase with Firebase
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth'; // Firebase auth methods
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'; // Firestore methods

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null, // Changed from session to user
    profile: null,
    loading: true,
    error: null,
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch user profile from Firestore
  const getUserProfile = async (user) => {
    if (!user) return null;

    console.log('Fetching profile for user:', user.uid);
    console.log('User email:', user.email);
    
    try {
      const profileDocRef = doc(db, 'profiles', user.uid);
      const profileDoc = await getDoc(profileDocRef);

      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        console.log('Profile fetched successfully:', profileData);
        
        // Sync Google profile picture if not already set and user has one
        if (!profileData.profile_picture_url && user.photoURL) {
          try {
            await setDoc(profileDocRef, { 
              ...profileData, 
              profile_picture_url: user.photoURL 
            }, { merge: true });
            console.log('Synced Google profile picture for existing user');
            return { ...profileData, profile_picture_url: user.photoURL };
          } catch (syncError) {
            console.error('Error syncing Google profile picture:', syncError);
            return profileData;
          }
        }
        
        return profileData;
      } else {
        console.warn('No profile found for user:', user.uid);
        // Create a default client profile if none exists
        const defaultProfile = {
          uid: user.uid,
          email: user.email,
          full_name: user.displayName || '',
          profile_picture_url: user.photoURL || null, // Use Google profile picture if available
          created_at: new Date(),
          is_admin: false,
          is_super_admin: false,
          is_writer: false,
          is_qa: false,
          is_client: true,
        };
        
        try {
          await setDoc(profileDocRef, defaultProfile);
          console.log('Created default client profile for user:', user.uid);
          return defaultProfile;
        } catch (createError) {
          console.error('Error creating default profile:', createError);
          // Return the default profile even if we can't save it
          return defaultProfile;
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      // Return a basic profile based on Firebase Auth user data if Firestore fails
      if (error.code === 'permission-denied' || error.code === 'unavailable') {
        console.warn('Firestore unavailable, using basic profile from auth data');
        return {
          uid: user.uid,
          email: user.email,
          full_name: user.displayName || '',
          profile_picture_url: user.photoURL || null, // Use Google profile picture if available
          created_at: new Date(),
          is_admin: false,
          is_super_admin: false,
          is_writer: false,
          is_qa: false,
          is_client: true,
        };
      }
      
      return null;
    }
  };

  useEffect(() => {
    console.log('AuthProvider mounted, setting up Firebase auth listener');
    let profileUnsubscribe = null;

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User found: ${user.uid}` : 'No user');
      try {
        setAuthState(prev => ({ ...prev, loading: true, error: null }));

        // Clean up previous profile listener
        if (profileUnsubscribe) {
          profileUnsubscribe();
          profileUnsubscribe = null;
        }

        if (user) {
          const profile = await getUserProfile(user);
          console.log('Setting auth state with user and profile');
          console.log('User object:', user);
          console.log('Profile object:', profile);
          setAuthState({ user, profile, loading: false, error: null });

          // Set up real-time listener for profile updates
          const profileDocRef = doc(db, 'profiles', user.uid);
          profileUnsubscribe = onSnapshot(profileDocRef, (doc) => {
            if (doc.exists()) {
              const updatedProfile = doc.data();
              console.log('Profile updated via real-time listener:', updatedProfile);
              setAuthState(prev => ({ ...prev, profile: updatedProfile }));
            }
          }, (error) => {
            console.error('Error in profile listener:', error);
          });
        } else {
          console.log('Setting auth state to logged out');
          setAuthState({ user: null, profile: null, loading: false, error: null });
        }
      } catch (error) {
        console.error('Error in auth state change listener:', error);
        setAuthState({ user: null, profile: null, loading: false, error });
      }
    });

    return () => {
      console.log('AuthProvider unmounting, cleaning up listeners');
      authUnsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, []);

  // Sign out function
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      setAuthState({ user: null, profile: null, loading: false, error: null });
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      setAuthState(prev => ({ ...prev, error }));
    }
  }, [navigate]);

  // Function to check if user is logged in and redirect if not
  const requireAuth = useCallback(() => {
    console.log('requireAuth called, checking user');
    console.log('Current auth state:', 
      authState.loading ? 'Loading' : authState.user ? 'User exists' : 'No user');

    if (authState.loading) {
      console.log('Auth state is still loading, not redirecting');
      return true; // Don't redirect while still loading
    }

    if (!authState.user) {
      console.log('No user found, redirecting to login');
      navigate('/login', { state: { from: location } });
      return false;
    }

    console.log('User is authenticated');
    return true;
  }, [authState.user, authState.loading, navigate, location]);

  // Role checking functions
  const isAdmin = useCallback(() => {
    return authState.profile?.is_admin === true || authState.profile?.is_super_admin === true;
  }, [authState.profile]);

  const isWriter = useCallback(() => {
    return authState.profile?.is_writer === true;
  }, [authState.profile]);

  const isQA = useCallback(() => {
    return authState.profile?.is_qa === true;
  }, [authState.profile]);

  const isClient = useCallback(() => {
    return authState.profile?.is_client === true;
  }, [authState.profile]);

    // Function to require a specific role
    const requireRole = useCallback((role, redirectPath = '/') => {
        if (!requireAuth()) {
            return false;
        }

        let hasRole = false;
        switch (role) {
            case 'admin':
                hasRole = isAdmin();
                break;
            case 'writer':
                hasRole = isWriter();
                break;
            case 'qa':
                hasRole = isQA();
                break;
            case 'client':
                hasRole = isClient();
                break;
            default:
                hasRole = false;
        }

        if (!hasRole) {
            navigate(redirectPath);
            return false;
        }

        return true;
    }, [requireAuth, isAdmin, isWriter, isQA, isClient, navigate]);


  // Value to be provided by the context
  const value = {
    ...authState,
    isLoggedIn: !!authState.user,
    signOut,
    requireAuth,
    isAdmin,
    isWriter,
    isQA,
    isClient,
    requireRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
